import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import Navbar from "@/components/Navbar";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Loader2, ArrowLeft, Flower2, Send, MessageSquareQuote, Repeat, Trash2, PauseCircle, PlayCircle } from "lucide-react";
import { toast } from "sonner";

const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const dayLabel = (iso) => {
  try {
    return new Date(`${iso}T12:00:00`).toLocaleDateString("en-AU", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  } catch { return iso; }
};

export default function FloristHistory() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState(null);
  const [standing, setStanding] = useState([]);

  useEffect(() => {
    api.get("/orders").then((r) => setOrders(r.data.filter((o) => o.service === "florist"))).catch(() => setOrders([]));
    api.get("/florist/standing-orders").then((r) => setStanding(r.data)).catch(() => {});
  }, []);

  const repeatWeekly = async (o) => {
    try {
      const { data } = await api.post("/florist/standing-orders", { order_id: o.id });
      setStanding((p) => [data, ...p]);
      toast.success(`Weekly delivery scheduled — every ${WEEKDAYS[data.weekday]} to ${o.dropoff.city}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't schedule that"); }
  };

  const toggleStanding = async (so) => {
    try {
      await api.patch(`/florist/standing-orders/${so.id}`, { active: !so.active });
      setStanding((p) => p.map((s) => (s.id === so.id ? { ...s, active: !so.active } : s)));
      toast.success(so.active ? "Weekly delivery paused" : "Weekly delivery resumed");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const removeStanding = async (so) => {
    try {
      await api.delete(`/florist/standing-orders/${so.id}`);
      setStanding((p) => p.filter((s) => s.id !== so.id));
      toast.success("Weekly delivery removed");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const resend = (o) => navigate("/book-flowers", {
    state: { resend: {
      dropoff: o.dropoff, recipient_name: o.recipient_name || o.dropoff.contact_name || "",
      recipient_phone: o.dropoff.contact_phone || "", card_message: o.card_message || "",
      flowers: o.parcel_description || "",
    } },
  });

  const groups = {};
  (orders || []).forEach((o) => {
    const key = o.scheduled_date || (o.created_at || "").slice(0, 10);
    (groups[key] = groups[key] || []).push(o);
  });
  const days = Object.keys(groups).sort().reverse();

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="mx-auto max-w-4xl px-4 py-8 sm:px-6">
        <Button variant="ghost" size="sm" onClick={() => navigate("/book-flowers")} data-testid="history-back-button" className="mb-4 gap-1.5">
          <ArrowLeft className="h-4 w-4" /> Back to booking
        </Button>
        <div className="flex items-center gap-2">
          <Flower2 className="h-7 w-7 text-pink-500" />
          <h1 className="font-heading text-3xl font-extrabold tracking-tight text-kilted-green">Flower delivery history</h1>
        </div>
        <p className="mt-1 text-sm text-muted-foreground">Your deliveries by day — tap "Send again" to rebook, or "Repeat weekly" for a standing delivery.</p>

        {standing.length > 0 && (
          <section className="mt-6" data-testid="standing-orders-section">
            <h2 className="mb-3 flex items-center gap-2 text-lg font-bold text-foreground"><Repeat className="h-5 w-5 text-pink-500" /> Weekly standing deliveries</h2>
            <div className="grid gap-2">
              {standing.map((so) => (
                <div key={so.id} className={`flex flex-wrap items-center gap-3 rounded-lg border p-3 ${so.active ? "border-pink-200 bg-pink-50/50" : "border-border bg-secondary/40 opacity-70"}`} data-testid={`standing-order-${so.id}`}>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-kilted-green">Every {WEEKDAYS[so.weekday]} — {so.recipient_name || "recipient"}, {so.dropoff.city}</p>
                    <p className="text-xs text-muted-foreground">{so.parcel_description}{so.card_message ? ` · "${so.card_message}"` : ""} · from {so.created_from}{so.active ? "" : " · paused"}</p>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => toggleStanding(so)} data-testid={`standing-toggle-${so.id}`} className="gap-1 text-muted-foreground">
                    {so.active ? <><PauseCircle className="h-4 w-4" /> Pause</> : <><PlayCircle className="h-4 w-4" /> Resume</>}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => removeStanding(so)} data-testid={`standing-remove-${so.id}`} className="gap-1 text-red-600 hover:bg-red-50 hover:text-red-700">
                    <Trash2 className="h-4 w-4" /> Remove
                  </Button>
                </div>
              ))}
            </div>
          </section>
        )}

        {!orders && <div className="mt-10 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>}
        {orders && !days.length && (
          <p className="mt-10 text-center text-muted-foreground" data-testid="history-empty">No flower deliveries yet — book your first one and it'll show up here.</p>
        )}

        {days.map((day) => (
          <section key={day} className="mt-8" data-testid={`history-day-${day}`}>
            <h2 className="mb-3 text-lg font-bold text-foreground">{dayLabel(day)}</h2>
            <div className="grid gap-3">
              {groups[day].map((o) => (
                <Card key={o.id} data-testid={`history-order-${o.tracking_number}`}>
                  <CardContent className="flex flex-col gap-3 pt-5 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <button onClick={() => navigate(`/orders/${o.id}`)} className="font-heading font-bold text-kilted-green hover:underline">{o.tracking_number}</button>
                        <StatusBadge status={o.status} />
                        <span className="text-sm font-semibold">${o.price.total.toFixed(2)}</span>
                      </div>
                      <p className="mt-1 text-sm">
                        <span className="font-medium">{o.recipient_name || o.dropoff.contact_name || "Recipient"}</span>
                        <span className="text-muted-foreground"> — {[o.dropoff.line1, o.dropoff.city].filter(Boolean).join(", ")}</span>
                      </p>
                      {o.parcel_description && <p className="mt-0.5 text-xs text-muted-foreground">{o.parcel_description}</p>}
                      {o.card_message && (
                        <p className="mt-2 flex items-start gap-1.5 rounded-md bg-pink-50 px-2.5 py-1.5 text-xs italic text-pink-800" data-testid={`history-card-message-${o.tracking_number}`}>
                          <MessageSquareQuote className="mt-0.5 h-3.5 w-3.5 shrink-0" /> "{o.card_message}"
                        </p>
                      )}
                    </div>
                    <div className="flex shrink-0 flex-col gap-2">
                      <Button size="sm" variant="outline" onClick={() => resend(o)} data-testid={`history-resend-${o.tracking_number}`}
                        className="gap-1.5 border-pink-300 text-pink-700 hover:bg-pink-50">
                        <Send className="h-3.5 w-3.5" /> Send again
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => repeatWeekly(o)} data-testid={`history-repeat-${o.tracking_number}`}
                        className="gap-1.5 border-kilted-gold text-kilted-green hover:bg-kilted-gold/10">
                        <Repeat className="h-3.5 w-3.5" /> Repeat weekly
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>
        ))}
      </main>
    </div>
  );
}
